import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { COLORS, Panel, Table, Button, Field, inputStyle, Spinner, Banner, StatTile, date } from '../components/ui';
import { ColumnChart } from '../components/charts';

/**
 * The exceptions view — the failure modes that are invisible in the MC UI.
 * Queries are ported from mc-pm/scripts/Test-PmHealth.ps1.
 */

const woLink = r => <Link to={`/wo/${r.WOPK}`} style={{ color: COLORS.link }}>{r.WOID}</Link>;

const COLUMNS = {
    'overdue-wos': [
        { key: 'WOID', label: 'WO', render: woLink },
        { key: 'ShopID', label: 'Site' }, { key: 'ShopName', label: 'Name' },
        { key: 'PMID', label: 'PM' }, { key: 'AssetID', label: 'Asset' },
        { key: 'Status', label: 'Status' }, { key: 'AccountID', label: 'GL' },
        { key: 'TargetDate', label: 'Target', render: r => date(r.TargetDate) },
        { key: 'DaysOverdue', label: 'Days late', align: 'right' },
    ],
    'complete-not-closed': [
        { key: 'WOID', label: 'WO', render: woLink },
        { key: 'ShopID', label: 'Site' }, { key: 'ShopName', label: 'Name' },
        { key: 'PMID', label: 'PM' }, { key: 'AssetID', label: 'Asset' },
        { key: 'Status', label: 'Status still says' },
        { key: 'Complete', label: 'Completed', render: r => date(r.Complete) },
        { key: 'DaysSinceComplete', label: 'Days unclosed', align: 'right' },
    ],
    'dormant-assets': [
        { key: 'PMID', label: 'PM' }, { key: 'PMName', label: 'Name', wrap: true },
        { key: 'ShopID', label: 'Site' }, { key: 'AssetID', label: 'Asset' },
        { key: 'NextScheduledDate', label: 'Next due', render: r => date(r.NextScheduledDate) },
        { key: 'LastGeneratedDate', label: 'Last raised', render: r => date(r.LastGeneratedDate) },
        { key: 'TimesDone', label: 'Times done', align: 'right' },
        { key: 'DaysOverdue', label: 'Days stalled', align: 'right' },
    ],
    'dead-pms': [
        { key: 'PMID', label: 'PM' }, { key: 'PMName', label: 'Name', wrap: true },
        { key: 'WOsEver', label: 'WOs ever', align: 'right', render: r => r.WOsEver === 0
            ? <span style={{ color: COLORS.critical, fontWeight: 600 }}>never</span> : r.WOsEver },
        { key: 'TimesDone', label: 'Times done', align: 'right' },
        { key: 'LastChanged', label: 'Last changed', render: r => date(r.LastChanged) },
        { key: 'LastChangedBy', label: 'By' },
        { key: 'DaysSinceChange', label: 'Days stale', align: 'right' },
    ],
    'disabled-pms': [
        { key: 'PMID', label: 'PM' }, { key: 'PMName', label: 'Name', wrap: true },
        { key: 'RepairCenterID', label: 'State' }, { key: 'Assets', label: 'Assets', align: 'right' },
        { key: 'LastGeneratedDate', label: 'Last raised', render: r => date(r.LastGeneratedDate) },
    ],
    'manual-pms': [
        { key: 'PMID', label: 'PM' }, { key: 'PMName', label: 'Name', wrap: true },
        { key: 'Assets', label: 'Assets', align: 'right' },
        { key: 'AutomationDaysPrior', label: 'Days prior', align: 'right' },
    ],
    'gl-drift': [
        { key: 'PMID', label: 'PM' }, { key: 'PMName', label: 'Name', wrap: true },
        { key: 'ShopID', label: 'Site' }, { key: 'AssetID', label: 'Asset' },
        { key: 'MajorityGL', label: 'Majority GL' }, { key: 'MajorityCount', label: 'n', align: 'right' },
        { key: 'OutlierGL', label: 'This row uses' }, { key: 'OutlierCount', label: 'n', align: 'right' },
    ],
    'retired-assets': [
        { key: 'PMID', label: 'PM' }, { key: 'ShopID', label: 'Site' },
        { key: 'AssetID', label: 'Asset' }, { key: 'AssetName', label: 'Name', wrap: true },
        { key: 'ClassificationID', label: 'Class' }, { key: 'ParentLocation', label: 'Parked under', wrap: true },
    ],
    'no-articles': [
        { key: 'PMID', label: 'PM' }, { key: 'PMName', label: 'Name', wrap: true },
        { key: 'ProcedurePK', label: 'Procedure' }, { key: 'Assets', label: 'Assets', align: 'right' },
    ],
    'no-evidence': [
        { key: 'WOID', label: 'WO', render: woLink },
        { key: 'ShopID', label: 'Site' }, { key: 'ShopName', label: 'Name' },
        { key: 'PMID', label: 'PM' }, { key: 'Status', label: 'Status' },
        { key: 'Complete', label: 'Completed', render: r => date(r.Complete) },
        { key: 'RequirementDocs', label: 'Requirement docs', align: 'right' },
    ],
};

export default function ExceptionsPage() {
    const today = new Date().toISOString().substring(0, 10);
    const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().substring(0, 10);

    const [from, setFrom] = useState(yearAgo);
    const [to, setTo] = useState(today);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState('overdue-wos');

    const load = useCallback(() => {
        setLoading(true); setError(null);
        api.pmExceptions({ from, to }).then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
    }, [from, to]);

    useEffect(() => { load(); }, [load]);

    const overdueSection = data?.sections.find(s => s.key === 'overdue-wos');
    const ageing = overdueSection ? (() => {
        const buckets = [
            { label: '1–30d', min: 1, max: 30 }, { label: '31–60d', min: 31, max: 60 },
            { label: '61–90d', min: 61, max: 90 }, { label: '91–180d', min: 91, max: 180 },
            { label: '180d+', min: 181, max: Infinity },
        ];
        return buckets.map(b => ({
            label: b.label,
            value: overdueSection.rows.filter(r => r.DaysOverdue >= b.min && r.DaysOverdue <= b.max).length,
            color: b.min > 90 ? COLORS.critical : b.min > 30 ? COLORS.serious : COLORS.series[0],
        }));
    })() : [];

    return (
        <div>
            <h1 style={{ fontSize: 22, margin: '0 0 4px', color: COLORS.ink }}>Exceptions</h1>
            <p style={{ color: COLORS.ink2, fontSize: 13, marginTop: 0 }}>
                The failure modes that do not show up in the MC UI — a PM can look perfectly live and generate nothing.
            </p>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
                <Field label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></Field>
                <Field label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></Field>
                <Button onClick={load}>Apply</Button>
            </div>

            {error && <Banner tone="danger">{error}</Banner>}
            {loading && <Spinner label="Auditing every active PM" />}

            {data && !loading && (
                <>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                        {data.sections.map(s => (
                            <div key={s.key} onClick={() => setOpen(s.key)} style={{ cursor: 'pointer' }}>
                                <StatTile
                                    label={s.title}
                                    value={s.truncated ? `${s.count}+` : s.count}
                                    tone={s.count === 0 ? 'good' : s.key === 'overdue-wos' || s.key === 'dormant-assets' ? 'bad' : 'warn'}
                                />
                            </div>
                        ))}
                    </div>

                    {ageing.some(b => b.value > 0) && (
                        <Panel title="Overdue ageing" subtitle={`How long overdue work orders have been late${overdueSection.truncated ? ` (first ${overdueSection.count} only)` : ''}`}>
                            <ColumnChart data={ageing} />
                        </Panel>
                    )}

                    {data.sections.map(s => (
                        <Panel
                            key={s.key}
                            title={`${s.title} — ${s.truncated ? `${s.count}+` : s.count}`}
                            subtitle={s.why}
                            right={<Button kind="ghost" onClick={() => setOpen(open === s.key ? null : s.key)}>
                                {open === s.key ? 'Hide' : 'Show'}
                            </Button>}
                        >
                            {s.truncated && <Banner tone="warn">{s.truncatedNote}</Banner>}
                            {open === s.key
                                ? <Table columns={COLUMNS[s.key] || []} rows={s.rows} empty="Nothing found — this check is clean" />
                                : <div style={{ fontSize: 12.5, color: COLORS.muted }}>
                                    {s.count === 0 ? 'Clean.' : `${s.count} row${s.count > 1 ? 's' : ''} — click Show.`}
                                </div>}
                        </Panel>
                    ))}
                </>
            )}
        </div>
    );
}
